import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Descriptions, Form, Input, message, Select, Skeleton} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {AiDefaultsServiceContext, AppContextContext} from "../../Contexts";
import AiDefaults, {AiDefaultEntry, AiDefaultValueType} from "../../domain/AiDefaults";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiDefaultsPage.module.css";
import {DocumentTitle} from "../DocumentTitle";

const FORM_LAYOUT = {
    labelCol: {span: 7},
    wrapperCol: {span: 17},
};

const FORM_TAIL_LAYOUT = {
    wrapperCol: {offset: 7, span: 17},
};

function AiDefaultsPage() {
    const appContext = useContext(AppContextContext);
    const aiDefaultsService = useContext(AiDefaultsServiceContext);
    const [form] = Form.useForm();
    const [defaults, setDefaults] = useState<AiDefaults>();
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        reload();
    }, [aiDefaultsService, form]);

    if (!defaults) {
        return <Skeleton/>;
    }

    const entries = defaults.entries ?? [];
    const sections = groupBySection(entries);

    return (
        <DocumentTitle title="AI defaults">
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item>Administration</Breadcrumb.Item>
                    <Breadcrumb.Item>AI defaults</Breadcrumb.Item>
                </Breadcrumb>

                <h1>AI defaults</h1>

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button onClick={() => setEditMode(!editMode)}>Edit</Button>
                    <Button onClick={reload}>Reload</Button>
                </div>

                {renderEditForm()}
                {renderDetails()}
            </>
        </DocumentTitle>
    );

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit AI defaults</h3>

                <Form
                    {...FORM_LAYOUT}
                    form={form}
                    className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`}
                    onFinish={onFinish}
                >
                    {Object.entries(sections).map(([section, sectionEntries]) => (
                        <React.Fragment key={section}>
                            <h3 className={styles.sectionTitle}>{section}</h3>
                            {sectionEntries.map(entry => renderFormItem(entry))}
                        </React.Fragment>
                    ))}

                    <Form.Item {...FORM_TAIL_LAYOUT} className={globalStyles["common__form-buttons"]}>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Save
                        </Button>
                        <Button onClick={cancelEdit}>
                            Cancel
                        </Button>
                    </Form.Item>
                </Form>
            </Collapse>
        );
    }

    function renderFormItem(entry: AiDefaultEntry) {
        return (
            <Form.Item
                key={entry.key}
                name={entry.key}
                label={entry.label}
                extra={entry.description}
                rules={entry.nullable ? [] : [{required: true, message: "Value is required."}]}
            >
                {renderInput(entry)}
            </Form.Item>
        );
    }

    function renderInput(entry: AiDefaultEntry) {
        switch (entry.valueType) {
            case AiDefaultValueType.TEXTAREA:
                return <Input.TextArea autoSize={{minRows: 2, maxRows: 8}} allowClear={true}/>;
            case AiDefaultValueType.JSON:
                return <Input.TextArea autoSize={{minRows: 8, maxRows: 20}} allowClear={true}/>;
            case AiDefaultValueType.ENUM:
            case AiDefaultValueType.AI_PROMPT:
            case AiDefaultValueType.AI_REPORT_SUMMARIZER:
            case AiDefaultValueType.ITEM_UPDATE_PROFILE:
                return (
                    <Select
                        allowClear={entry.nullable}
                        showSearch={true}
                        optionFilterProp="label"
                        options={(entry.options ?? []).map(option => ({
                            value: option.value,
                            label: option.label
                        }))}
                    />
                );
            default:
                return <Input allowClear={entry.nullable}/>;
        }
    }

    function renderDetails() {
        return (
            <>
                {Object.entries(sections).map(([section, sectionEntries]) => (
                    <div className={styles.section} key={section}>
                        <h3 className={styles.sectionTitle}>{section}</h3>
                        <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                            {sectionEntries.map(entry => (
                                <Descriptions.Item key={entry.key} label={entry.label}>
                                    {renderValue(entry)}
                                    <div className={styles.description}>{entry.description}</div>
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </div>
                ))}
            </>
        );
    }

    function renderValue(entry: AiDefaultEntry) {
        const value = entry.value;

        if (value == null || value === "") {
            return <i className={styles.unset}>not set</i>;
        }

        if (entry.valueType === AiDefaultValueType.JSON) {
            return <pre className={styles.jsonValue}>{value}</pre>;
        }

        return findOptionLabel(entry, value) ?? value;
    }

    function findOptionLabel(entry: AiDefaultEntry, value: string) {
        return entry.options?.find(option => option.value === value)?.label;
    }

    function cancelEdit() {
        form.setFieldsValue(valuesFromEntries(entries));
        setEditMode(false);
    }

    function onFinish(rawValues: Record<string, string | undefined>) {
        const values: Record<string, string | null> = {};

        for (const entry of entries) {
            if (!entry.key) continue;

            values[entry.key] = rawValues[entry.key] ?? null;
        }

        setSaving(true);

        aiDefaultsService.update(values)
            .then(() => {
                message.success("AI defaults updated.");
                setEditMode(false);
                reload();
            })
            .finally(() => setSaving(false));
    }

    function reload() {
        aiDefaultsService.get()
            .then(value => {
                setDefaults(value);
                form.setFieldsValue(valuesFromEntries(value.entries ?? []));
            });
    }
}

function groupBySection(entries: AiDefaultEntry[]) {
    const result: Record<string, AiDefaultEntry[]> = {};

    for (const entry of entries) {
        const section = entry.section ?? "Other";

        if (!result[section]) {
            result[section] = [];
        }

        result[section].push(entry);
    }

    return result;
}

function valuesFromEntries(entries: AiDefaultEntry[]) {
    const result: Record<string, string | null | undefined> = {};

    for (const entry of entries) {
        if (!entry.key) continue;

        result[entry.key] = entry.value;
    }

    return result;
}

export default AiDefaultsPage;
